// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Pulling the model's shape out of its metadata.
 *
 * GGUF namespaces architecture keys by the architecture's own name
 * (`qwen3.block_count`, `llama.attention.head_count_kv`), so everything here
 * resolves through `general.architecture` first. Where a key is absent the
 * documented default is applied and recorded as an assumption, because an
 * assumed head dimension is exactly the thing that makes other calculators
 * wrong by gigabytes.
 */

import { metaNumber, metaNumberOrArray, metaString } from './gguf/parse';
import type { GgufValue } from './gguf/types';

export interface Geometry {
  arch: string;
  name?: string;
  nLayer: number;
  nEmbd: number;
  nHead: number;
  /** KV heads per layer. Uniform models still get one entry per layer. */
  nHeadKv: number[];
  /** Per-head key dimension. */
  headK: number;
  /** Per-head value dimension. */
  headV: number;
  ctxTrain?: number;
  vocab?: number;
  expertCount?: number;
  expertUsed?: number;
  /** Set when the architecture uses sliding-window attention on some layers. */
  slidingWindow?: number;
  /** Assumptions the resolver had to make, shown to the user verbatim. */
  assumptions: string[];
  /** Conditions that make the KV estimate unreliable. */
  cautions: string[];
}

export class GeometryError extends Error {}

/**
 * Architectures whose KV cache is not `head_dim × n_head_kv` per layer.
 * DeepSeek's MLA compresses K and V into a single latent vector, so the
 * standard formula overstates the cache by a large factor — we say so rather
 * than print a confident wrong number.
 */
const MLA_ARCHS = new Set(['deepseek2', 'deepseek3', 'kimi_k2']);

export function resolveGeometry(kv: Map<string, GgufValue>): Geometry {
  const arch = metaString(kv, 'general.architecture');
  if (!arch) throw new GeometryError('This file has no general.architecture key, so its shape cannot be read.');

  const assumptions: string[] = [];
  const cautions: string[] = [];
  const k = (suffix: string) => `${arch}.${suffix}`;

  const nLayer = metaNumber(kv, k('block_count'));
  if (!nLayer) throw new GeometryError(`Missing ${k('block_count')} — the layer count is required to size anything.`);

  const nEmbd = metaNumber(kv, k('embedding_length')) ?? 0;
  const nHead = metaNumber(kv, k('attention.head_count')) ?? 0;

  // head_count_kv may legitimately be absent (then it equals head_count, i.e.
  // no grouped-query attention) or a per-layer array on hybrid architectures.
  const kvHeads = metaNumberOrArray(kv, k('attention.head_count_kv'));
  let nHeadKv: number[];
  if (!kvHeads) {
    nHeadKv = new Array(nLayer).fill(nHead);
    if (nHead) assumptions.push(`${k('attention.head_count_kv')} is absent, so every head is assumed to have its own KV (no GQA).`);
  } else if ('uniform' in kvHeads) {
    nHeadKv = new Array(nLayer).fill(kvHeads.uniform);
  } else {
    nHeadKv = kvHeads.perLayer.slice(0, nLayer);
    while (nHeadKv.length < nLayer) nHeadKv.push(nHeadKv[nHeadKv.length - 1] ?? nHead);
    const distinct = new Set(nHeadKv);
    if (distinct.size > 1) {
      assumptions.push(`KV heads vary per layer (${Math.min(...nHeadKv)}–${Math.max(...nHeadKv)}); the cache is summed layer by layer.`);
    }
  }

  // key_length / value_length are explicit on most modern models and are
  // exactly where the naive `embedding_length / head_count` shortcut breaks:
  // Qwen3-32B has 5120/64 = 80 but a real key_length of 128, a 60% difference
  // in the cache.
  let headK = metaNumber(kv, k('attention.key_length'));
  let headV = metaNumber(kv, k('attention.value_length'));
  if (headK === undefined || headV === undefined) {
    if (!nEmbd || !nHead) {
      throw new GeometryError(
        `This model declares neither ${k('attention.key_length')} nor enough shape metadata to derive it, so the KV cache cannot be sized honestly.`,
      );
    }
    const derived = Math.floor(nEmbd / nHead);
    if (headK === undefined) headK = derived;
    if (headV === undefined) headV = derived;
    assumptions.push(
      `key_length/value_length are absent, so the head dimension falls back to embedding_length ÷ head_count = ${derived}.`,
    );
  }

  const geo: Geometry = {
    arch,
    name: metaString(kv, 'general.name'),
    nLayer,
    nEmbd,
    nHead,
    nHeadKv,
    headK,
    headV,
    ctxTrain: metaNumber(kv, k('context_length')),
    vocab: metaNumber(kv, k('vocab_size')),
    expertCount: metaNumber(kv, k('expert_count')),
    expertUsed: metaNumber(kv, k('expert_used_count')),
    slidingWindow: metaNumber(kv, k('attention.sliding_window')),
    assumptions,
    cautions,
  };

  if (MLA_ARCHS.has(arch)) {
    cautions.push(
      `${arch} uses multi-head latent attention, which compresses K and V into one small latent vector instead of storing them per head. The standard formula below will substantially OVERSTATE its KV cache — treat the cache figure as an upper bound only.`,
    );
  }
  if (geo.slidingWindow) {
    cautions.push(
      `This model uses sliding-window attention (window ${geo.slidingWindow.toLocaleString('en')}). llama.cpp allocates a smaller cache for the windowed layers, so at long context the real KV usage is LOWER than shown here — this figure is a safe upper bound.`,
    );
  }
  if (geo.expertCount && geo.expertCount > 1) {
    geo.assumptions.push(
      `Mixture of experts: ${geo.expertCount} experts, ${geo.expertUsed ?? '?'} active per token. Every expert still has to be resident, so the weight figure is the full count.`,
    );
  }

  return geo;
}

/**
 * llama.cpp rounds the requested context up to a multiple of 256 before
 * allocating (`cparams.n_ctx = GGML_PAD(cparams.n_ctx, 256)`), so a request for
 * 4000 tokens actually allocates 4096 worth of cache.
 */
export function padContext(nCtx: number): number {
  return Math.ceil(nCtx / 256) * 256;
}
